import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import API_BASE_URL from "../config";
import { getTrustedDeviceToken, setAuthSession } from "../utils/authStorage";

const TURNSTILE_SITE_KEY = "0x4AAAAAADdZNKLR1wrD1SK5";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loginLogoUrl, setLoginLogoUrl] = useState("");
  const [logoLoaded, setLogoLoaded] = useState(false);

  const navigate = useNavigate();
  const turnstileRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);

  const hostname = window.location.hostname.toLowerCase();

  const isSubscriberSubdomain = useMemo(() => {
    return (
      hostname !== "agentbookcrm.com" &&
      hostname !== "www.agentbookcrm.com" &&
      hostname.endsWith(".agentbookcrm.com")
    );
  }, [hostname]);

  const heading = "Sign in to Agent Book CRM";

  const subheading = isSubscriberSubdomain
    ? "Access your agency workspace securely."
    : "Access your Agent Book CRM workspace securely.";

  const resetTurnstile = () => {
    setTurnstileToken("");

    if (window.turnstile && turnstileWidgetIdRef.current !== null) {
      try {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      } catch (err) {
        console.error("Failed to reset Cloudflare verification:", err);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    async function loadLoginLogo() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/platform-settings`);
        const data = await res.json();

        if (isMounted) {
          setLoginLogoUrl(data?.login_logo_url || "");
        }
      } catch (err) {
        console.error("Failed to load login logo:", err);
      } finally {
        if (isMounted) {
          setLogoLoaded(true);
        }
      }
    }

    loadLoginLogo();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const renderTurnstile = () => {
      if (!window.turnstile || !turnstileRef.current) {
        return;
      }

      if (turnstileWidgetIdRef.current !== null) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        callback: (token) => {
          setTurnstileToken(token || "");
        },
        "expired-callback": () => {
          setTurnstileToken("");
        },
        "error-callback": () => {
          setTurnstileToken("");
        },
      });
    };

    if (window.turnstile) {
      renderTurnstile();
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", renderTurnstile);
      return () => existingScript.removeEventListener("load", renderTurnstile);
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", renderTurnstile);

    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", renderTurnstile);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!turnstileToken) {
      setErrorMsg("Please complete the verification check before signing in.");
      return;
    }

    try {
      setSubmitting(true);

      const trustedDeviceToken = getTrustedDeviceToken();

      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(trustedDeviceToken
            ? { "x-trusted-device-token": trustedDeviceToken }
            : {}),
        },
        body: JSON.stringify({
          email,
          password,
          trustedDeviceToken: trustedDeviceToken || null,
          turnstile_token: turnstileToken,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        resetTurnstile();
        throw new Error(data.message || "Login failed");
      }

      if (data.requiresTwoFactor) {
        const pendingPayload = {
          challengeId: data.challengeId,
          emailMasked: data.emailMasked,
          email,
        };

        sessionStorage.setItem("pending_2fa", JSON.stringify(pendingPayload));

        navigate("/verify-login", {
          replace: true,
          state: pendingPayload,
        });

        return;
      }

      if (data.token) {
        const redirectPath =
          sessionStorage.getItem("redirect_after_login") || "/dashboard";

        setAuthSession({
          token: data.token,
          user: data.user || {},
          demoSession: null,
          trustedDeviceToken: data.trustedDeviceToken || trustedDeviceToken || null,
        });

        sessionStorage.removeItem("pending_2fa");
        sessionStorage.removeItem("redirect_after_login");

        navigate(redirectPath, { replace: true });
        return;
      }

      resetTurnstile();
      throw new Error("Unexpected login response");
    } catch (err) {
      console.error("Login error:", err.message);
      setErrorMsg(err.message || "Unable to sign in.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-background-orb auth-background-orb-one" />
      <div className="auth-background-orb auth-background-orb-two" />

      <div className="auth-card auth-card-login">
        <div className="auth-brand">
          <div className="auth-brand-logo auth-brand-logo-image" aria-label="Agent Book CRM logo">
            {logoLoaded && loginLogoUrl ? (
              <img src={loginLogoUrl} alt="Agent Book CRM Logo" />
            ) : (
              <div className="auth-brand-logo-placeholder" />
            )}
          </div>

          <div className="auth-brand-copy">
            <p className="auth-brand-kicker">Agent Book CRM</p>
            <h1 className="auth-brand-title">{heading}</h1>
            <p className="auth-brand-subtitle">{subheading}</p>
          </div>
        </div>

        <form className="auth-form" onSubmit={handleLogin}>
          <div className="auth-form-section">
            <div className="auth-field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoComplete="email"
                required
              />
            </div>

            <div className="auth-field">
              <div className="auth-label-row">
                <label>Password</label>

                <button
                  type="button"
                  className="auth-text-link"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot password?
                </button>
              </div>

              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
            </div>
          </div>

          {errorMsg && <div className="auth-error-banner">{errorMsg}</div>}

          <button
            type="submit"
            className="auth-primary-btn"
            disabled={submitting || !turnstileToken}
          >
            {submitting ? "Signing in..." : "Sign In"}
          </button>

          <div className="auth-turnstile-wrap">
            <div ref={turnstileRef} />
          </div>

          <div className="auth-footer-note">
            Secure sign-in includes a one-time email verification code on untrusted devices.
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;